import { useEffect, useRef } from "react"
import classNames from "classnames"
import { playSliderToggle } from "@/utils/audioManager"

interface MobileCashSheetProps {
  amount: number
  // What the payer holds — the ceiling for every control in here.
  max: number
  payerName: string
  payerKeepsVerb: string
  paysVerb: string
  payerLeft: number
  receiverName: string
  receiverHasCash: boolean
  draft: string | null
  onInput: (raw: string) => void
  onInputBlur: () => void
  setAmount: (value: number) => void
  // Omitted when the direction is locked (one side is in debt), exactly like
  // the drawer's own Swap button.
  onSwap?: () => void
  swapLabel: string
  onDone: () => void
}

// Amounts a trade is actually written in. Properties cost 60-400 and players
// hold ~1200-2000, so every realistic figure is one to three taps from here —
// which is the whole reason this replaces the slider on a phone rather than
// just growing it. A 100px track carrying 200 steps can't be aimed at all; a
// chip doesn't have to be.
const DELTAS = [-100, -50, -10, 10, 50, 100]

// Everything is sized in raw px on purpose. index.css halves the root font size
// below 1000px wide, so rem-based utilities collapse to half a touch target
// here — the same reason ModalCloseButton is written in px.
const CHIP =
  "flex h-[32px] items-center justify-center rounded-lg font-poppins font-semibold text-[13px] leading-none transition-colors select-none touch-none"

/**
 * The cash editor, as a sheet over the trade drawer's own box.
 *
 * On a phone the drawer is the left sidebar — about 200px wide — and the cash
 * card's slider row ends up with ~100px of track, a 12px thumb and 200 steps on
 * it. Rather than shrink that further, the amount stops being a strip inside
 * the deal and becomes a step of its own: the drawer collapses cash to a single
 * tappable row, and this takes the whole 200x296 box to edit it in. Same
 * footprint over the board, roughly six times the area for the one control that
 * needed it.
 *
 * It is `absolute inset-0` inside the drawer rather than a new fixed layer, so
 * it can never be taller than the box it covers and useFitTradeCards — which
 * measures scrollHeight against clientHeight — doesn't see it at all.
 */
const MobileCashSheet: React.FC<MobileCashSheetProps> = ({
  amount,
  max,
  payerName,
  payerKeepsVerb,
  paysVerb,
  payerLeft,
  receiverName,
  receiverHasCash,
  draft,
  onInput,
  onInputBlur,
  setAmount,
  onSwap,
  swapLabel,
  onDone,
}) => {
  const canPay = max > 0
  const clamp = (value: number) => Math.min(Math.max(value, 0), max)

  // The repeat below runs off a ref rather than the prop: between two ticks
  // 90ms apart the parent has re-rendered from redux, but a held button that
  // read `amount` from its own closure would keep adding to the value it was
  // pressed on.
  const amountRef = useRef(amount)
  amountRef.current = amount

  const holdRef = useRef<{ delay?: number; repeat?: number }>({})
  const stopHold = () => {
    window.clearTimeout(holdRef.current.delay)
    window.clearInterval(holdRef.current.repeat)
    holdRef.current = {}
  }
  // A finger lifted outside the button, a re-render, an unmount mid-press:
  // whatever ends the press, the timers have to go with it.
  useEffect(() => stopHold, [])

  const bump = (delta: number, sound: boolean) => {
    const next = clamp(amountRef.current + delta)
    if (next === amountRef.current) return false
    amountRef.current = next
    setAmount(next)
    if (sound) playSliderToggle()
    return true
  }

  // Tap for one step, hold to run. The chips alone cover round numbers; holding
  // is what makes the odd amount in between — M340, M615 — reachable without
  // opening the keyboard.
  const startHold = (delta: number) => {
    if (!bump(delta, true)) return
    holdRef.current.delay = window.setTimeout(() => {
      holdRef.current.repeat = window.setInterval(() => {
        // Silent while repeating: one tick per 90ms would be a machine gun.
        if (!bump(delta, false)) stopHold()
      }, 90)
    }, 350)
  }

  const holdProps = (delta: number) => ({
    onPointerDown: () => startHold(delta),
    onPointerUp: stopHold,
    onPointerLeave: stopHold,
    onPointerCancel: stopHold,
  })

  const preset = (value: number) => {
    const next = clamp(value)
    if (next === amount) return
    setAmount(next)
    playSliderToggle()
  }

  return (
    <div
      data-testid="trade.cash-sheet"
      // Opaque and edge to edge inside the drawer: this is a step, not an
      // overlay to read the deal through.
      //
      // Safe area: an absolutely positioned box is laid out against its
      // ancestor's *padding* box, so `inset-0` covers the very gutter the
      // drawer holds back with pl/pb — the sheet's own controls would sit under
      // the notch and the home indicator. Taken here the same way the drawer
      // takes it: as padding on top of the sheet's own, so the surface still
      // runs to the screen's edge and only the content is held inside the safe
      // box, with the gutter left empty.
      className={classNames(
        "absolute inset-0 z-10 flex flex-col bg-[#14121b] p-[8px]",
        "pl-[calc(8px_+_var(--safe-left))] pb-[calc(8px_+_var(--safe-bottom))]"
      )}
    >
      {/* Both ways out say the same thing. The chevron is where a phone user
          reaches first; Done is what someone who read the sheet as a form looks
          for. Nothing is committed by either — the amount is already in the
          draft as it's typed, same as the desktop card. */}
      <div className="flex shrink-0 items-center gap-[6px]">
        <button
          type="button"
          onClick={onDone}
          aria-label="Back to the deal"
          data-testid="trade.cash-sheet-back"
          className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-lg bg-white/5 text-white/70 active:scale-95"
        >
          <i className="fa-solid fa-chevron-left text-[13px]" />
        </button>
        <span className="flex min-w-0 flex-1 items-center gap-[6px] font-poppins font-bold uppercase tracking-[0.09em] text-[11px] text-amber-200">
          <i className="fa-solid fa-coins text-[11px] text-amber-300" />
          Cash
        </span>
        <button
          type="button"
          onClick={onDone}
          data-testid="trade.cash-sheet-done"
          className="flex h-[32px] shrink-0 items-center rounded-lg border border-amber-300/40 bg-amber-400/20 px-[12px] font-poppins font-semibold text-[12px] text-amber-200 active:scale-95"
        >
          Done
        </button>
      </div>

      {/* Scrolls only as a last resort. The drawer is pinned to its full height
          cap while this is up (see cashSheetOpen in CreateTradeModal), which on
          every phone the game is playable on is enough for all of this — but a
          box shorter than the chips should scroll them, not cut them off. */}
      <div className="flex min-h-0 flex-1 flex-col gap-[6px] overflow-y-auto pt-[6px]">
        {/* The sentence the drawer's collapsed row was showing, minus the
            amount — which is the field right underneath it. */}
        <div className="shrink-0 truncate text-center font-poppins text-[12px] leading-none text-white/55">
          <span className="font-semibold text-white">{payerName}</span>{" "}
          {paysVerb} <span className="font-semibold text-white">{receiverName}</span>
        </div>

        {canPay ? (
          <>
            <div className="flex shrink-0 items-center justify-center">
              <span
                className={classNames(
                  "inline-flex items-center gap-[2px] rounded-xl border px-[10px] py-[4px]",
                  amount > 0
                    ? "border-amber-300/40 bg-black/40 focus-within:border-amber-300"
                    : "border-white/10 bg-black/30"
                )}
              >
                <span
                  className={classNames(
                    "font-fira-sans text-[18px] leading-none",
                    amount > 0 ? "text-amber-300" : "text-white/40"
                  )}
                >
                  M
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={draft ?? String(amount)}
                  onChange={(e) => onInput(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onBlur={onInputBlur}
                  aria-label="Cash amount"
                  data-testid="trade.cash-sheet-amount"
                  className={classNames(
                    "h-[28px] w-[104px] bg-transparent text-center font-poppins font-bold text-[24px] leading-none outline-none",
                    amount > 0 ? "text-white" : "text-white/40"
                  )}
                />
              </span>
            </div>

            <div
              data-testid="trade.cash-sheet-after"
              className="shrink-0 truncate text-center font-poppins text-[11px] leading-none text-white/50"
            >
              {payerName} {payerKeepsVerb}{" "}
              <span
                className={classNames(
                  "font-bold",
                  payerLeft <= 0
                    ? "text-red-300"
                    : payerLeft < 100
                    ? "text-amber-300"
                    : "text-emerald-300"
                )}
              >
                M{payerLeft}
              </span>
            </div>

            {/* Two rows of steps and one of shortcuts. Every target is 34px
                tall and a third of the drawer wide — nothing in here has to be
                aimed at. */}
            <div className="grid shrink-0 grid-cols-3 gap-[5px]">
              {DELTAS.map((delta) => {
                const dead = clamp(amount + delta) === amount
                return (
                  <button
                    key={delta}
                    type="button"
                    disabled={dead}
                    aria-label={`${delta > 0 ? "Add" : "Subtract"} ${Math.abs(
                      delta
                    )}`}
                    data-testid="trade.cash-sheet-step"
                    {...holdProps(delta)}
                    className={classNames(CHIP, {
                      "bg-amber-400/20 text-amber-200 active:bg-amber-400/30":
                        !dead && delta > 0,
                      "bg-white/[0.09] text-white active:bg-white/[0.14]":
                        !dead && delta < 0,
                      "bg-white/5 text-white/25": dead,
                    })}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </button>
                )
              })}
            </div>

            <div className="grid shrink-0 grid-cols-3 gap-[5px]">
              <button
                type="button"
                disabled={amount === 0}
                onClick={() => preset(0)}
                data-testid="trade.cash-sheet-preset"
                className={classNames(
                  CHIP,
                  amount === 0
                    ? "bg-white/5 text-white/25"
                    : "bg-white/[0.09] text-white active:bg-white/[0.14]"
                )}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => preset(Math.floor(max / 20) * 10)}
                title={`Half of what ${payerName} ${payerKeepsVerb}`}
                data-testid="trade.cash-sheet-preset"
                className={classNames(
                  CHIP,
                  "bg-white/[0.09] text-white active:bg-white/[0.14]"
                )}
              >
                Half
              </button>
              <button
                type="button"
                disabled={amount === max}
                onClick={() => preset(max)}
                data-testid="trade.cash-sheet-preset"
                className={classNames(
                  CHIP,
                  amount === max
                    ? "bg-white/5 text-white/25"
                    : "bg-amber-400/20 text-amber-200 active:bg-amber-400/30"
                )}
              >
                All in
              </button>
            </div>
          </>
        ) : (
          // Same dead-end note the drawer shows, kept here because this is now
          // where a player goes looking for the amount.
          <div className="flex shrink-0 items-start gap-[6px] rounded-lg border border-white/10 bg-black/30 p-[8px]">
            <i className="fa-solid fa-circle-info mt-[2px] shrink-0 text-[11px] text-amber-300/80" />
            <span className="min-w-0 font-poppins text-[12px] leading-snug text-amber-100/80">
              {receiverHasCash ? (
                <>
                  {payerName} {payerKeepsVerb === "keep" ? "have" : "has"} no
                  cash — use Swap to send it the other way.
                </>
              ) : (
                <>Neither player has cash to put into this deal.</>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Same keycap as the drawer, same words: pressing it turns the sentence
          at the top of this sheet into the one written on it. */}
      {onSwap && (
        <button
          type="button"
          onClick={onSwap}
          data-testid="trade.cash-sheet-swap"
          className={classNames(
            "mt-[6px] flex h-[38px] shrink-0 items-center gap-[8px] rounded-lg border px-[8px] font-poppins leading-none",
            "border-sky-600 bg-sky-800 text-sky-50 shadow-[0_3px_0_#0c2b3f]",
            "transition-transform duration-100 active:translate-y-[2px] active:shadow-[0_1px_0_#0c2b3f]"
          )}
        >
          <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-md bg-black/25">
            <i aria-hidden className="fa-solid fa-right-left text-[11px]" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col items-start gap-[3px] text-left">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.09em]">
              Swap
            </span>
            <span className="w-full min-w-0 truncate text-[10px] font-medium text-sky-100/70">
              {swapLabel}
            </span>
          </span>
          <i
            aria-hidden
            className="fa-solid fa-chevron-right shrink-0 text-[10px] text-sky-100/50"
          />
        </button>
      )}
    </div>
  )
}

export default MobileCashSheet
